import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import SeoHead from "@/components/seo/SeoHead";
import Link from "next/link";
import { breadcrumbJsonLd, seoForReport } from "@/lib/seo";
import { motion } from "framer-motion";
import { FileText, Lock, Download, Copy, Check, ArrowLeft } from "lucide-react";
import { REPORTS_DB, getReportBySlug, getRelatedReports } from "@/data/reportsData";
const generateCitation = (report, style) => {
  const {
    author,
    title,
    organization,
    year
  } = report;
  switch (style) {
    case "APA":
      return `${author}. (${year}). ${title}. ${organization}.`;
    case "MLA":
      return `${author}. "${title}." ${organization}, ${year}.`;
    case "Chicago":
      return `${author}. "${title}." ${organization}, ${year}.`;
    default:
      return "";
  }
};
export default function ReportDetailPage(t0) {
  const $ = _c(97);
  const {
    report,
    relatedReports
  } = t0;
  const [citationStyle, setCitationStyle] = useState("APA");
  const [copied, setCopied] = useState(false);
  if (!report) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center"><div className="text-center"><h1 className="font-display text-[32px] sm:text-[40px] md:text-[48px] text-[#0F1C2E] mb-4">Report Not Found</h1><Link href="/reports" className="font-body text-[#DF6D2A] underline">Return to Reports</Link></div></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== citationStyle || $[2] !== report) {
    t1 = generateCitation(report, citationStyle);
    $[1] = citationStyle;
    $[2] = report;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const citation = t1;
  let t2;
  if ($[4] !== citation) {
    t2 = () => {
      navigator.clipboard.writeText(citation);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    $[4] = citation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleCopy = t2;
  let t3;
  if ($[6] !== report) {
    t3 = seoForReport(report);
    $[6] = report;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== report.slug || $[9] !== report.title) {
    t4 = breadcrumbJsonLd([{
      name: "Home",
      path: "/"
    }, {
      name: "Reports",
      path: "/reports"
    }, {
      name: report.title,
      path: `/reports/${report.slug}`
    }]);
    $[8] = report.slug;
    $[9] = report.title;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <SeoHead {...t3} jsonLd={t4} />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link href="/reports" className="font-body text-[11px] tracking-[0.15em] uppercase text-[#DF6D2A] hover:text-[#0F1C2E] transition-colors flex items-center gap-2"><ArrowLeft size={14} /> All Reports</Link>;
    t7 = <span className="text-[#0F1C2E]/20">/</span>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  let t8;
  if ($[16] !== report.id) {
    t8 = <section className="bg-[#EBE5D9] border-b border-[#0F1C2E]/10 py-4 sm:py-6"><div className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-12 flex items-center gap-2 sm:gap-3">{t6}{t7}<span className="font-body text-[11px] tracking-[0.1em] uppercase text-[#4C596B] truncate">{report.id}</span></div></section>;
    $[16] = report.id;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== report.type) {
    t9 = <span className="font-body text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 bg-[#0F1C2E] text-white">{report.type}</span>;
    $[18] = report.type;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `font-body text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 border flex items-center gap-1.5 ${report.access === "Open Access" ? "border-green-600/30 text-green-700" : "border-red-500/30 text-red-600"}`;
  let t11;
  if ($[20] !== report.access) {
    t11 = report.access === "Open Access" ? <FileText size={10} /> : <Lock size={10} />;
    $[20] = report.access;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== report.access || $[23] !== t10 || $[24] !== t11) {
    t12 = <span className={t10}>{t11}{report.access}</span>;
    $[22] = report.access;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t9) {
    t13 = <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">{t9}{t12}</div>;
    $[26] = t12;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  let t15;
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      y: 20
    };
    t15 = {
      opacity: 1,
      y: 0
    };
    t16 = {
      duration: 0.6
    };
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t14 = $[29];
    t15 = $[30];
    t16 = $[31];
  }
  let t17;
  if ($[32] !== report.title) {
    t17 = <motion.h1 initial={t14} animate={t15} transition={t16} className="font-display text-[28px] sm:text-[36px] md:text-[48px] leading-[1.1] text-[#0F1C2E] mb-4 sm:mb-6">{report.title}</motion.h1>;
    $[32] = report.title;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="font-body text-[9px] uppercase tracking-widest text-[#4C596B] mb-0.5">Author</p>;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== report.author) {
    t19 = <div>{t18}<p className="font-body text-[14px] sm:text-[16px] text-[#0F1C2E]">{report.author}</p></div>;
    $[35] = report.author;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <p className="font-body text-[9px] uppercase tracking-widest text-[#4C596B] mb-0.5">Organization</p>;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== report.organization) {
    t21 = <div>{t20}<p className="font-body text-[14px] sm:text-[16px] text-[#0F1C2E]">{report.organization}</p></div>;
    $[38] = report.organization;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="font-body text-[9px] uppercase tracking-widest text-[#4C596B] mb-0.5">Year</p>;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== report.year) {
    t23 = <div>{t22}<p className="font-body text-[14px] sm:text-[16px] text-[#0F1C2E]">{report.year}</p></div>;
    $[41] = report.year;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <p className="font-body text-[9px] uppercase tracking-widest text-[#4C596B] mb-0.5">Country</p>;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== report.country) {
    t25 = <div>{t24}<p className="font-body text-[14px] sm:text-[16px] text-[#DF6D2A]">{report.country}</p></div>;
    $[44] = report.country;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t19 || $[47] !== t21 || $[48] !== t23 || $[49] !== t25) {
    t26 = <div className="flex flex-wrap gap-x-5 sm:gap-x-8 gap-y-3 mb-8 sm:mb-10 pb-8 sm:pb-10 border-b border-[#0F1C2E]/10">{t19}{t21}{t23}{t25}</div>;
    $[46] = t19;
    $[47] = t21;
    $[48] = t23;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <h2 className="font-display text-[20px] sm:text-[24px] text-[#0F1C2E] mb-3 sm:mb-4">Abstract</h2>;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== report.abstract) {
    t28 = <div className="mb-8 sm:mb-12">{t27}<p className="font-body text-[14px] sm:text-[16px] text-[#4C596B] leading-[1.7] sm:leading-[1.9]">{report.abstract}</p></div>;
    $[52] = report.abstract;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <h3 className="font-body text-[10px] uppercase tracking-widest text-[#4C596B] mb-3">{"Keywords & Tags"}</h3>;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== report.tags) {
    t30 = report.tags.map(_temp);
    $[55] = report.tags;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== t30) {
    t31 = <div className="mb-8 sm:mb-12">{t29}<div className="flex flex-wrap gap-2">{t30}</div></div>;
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <h3 className="font-display text-[18px] sm:text-[22px] text-[#0F1C2E] mb-4 sm:mb-6">Cite This Report</h3>;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = ["APA", "MLA", "Chicago"];
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== citationStyle) {
    t34 = <div className="flex flex-wrap gap-2 mb-4">{t33.map(style => <button key={style} onClick={() => setCitationStyle(style)} className={`font-body text-[10px] sm:text-[11px] tracking-[0.1em] uppercase px-3 sm:px-4 py-2 border transition-colors ${citationStyle === style ? "bg-[#0F1C2E] text-white border-[#0F1C2E]" : "border-[#0F1C2E]/15 text-[#4C596B] hover:bg-[#EBE5D9]"}`}>{style}</button>)}</div>;
    $[61] = citationStyle;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== copied) {
    t35 = copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-[#4C596B]" />;
    $[63] = copied;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== handleCopy || $[66] !== t35) {
    t36 = <button onClick={handleCopy} className="absolute top-3 right-3 p-2 hover:bg-[#0F1C2E]/5 transition-colors rounded" title="Copy citation">{t35}</button>;
    $[65] = handleCopy;
    $[66] = t35;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] !== citation || $[69] !== t36) {
    t37 = <div className="bg-[#F4EFE6] border border-[#0F1C2E]/10 p-3 sm:p-4 font-body text-[13px] sm:text-[14px] text-[#0F1C2E] leading-[1.6] sm:leading-[1.7] italic relative pr-10 sm:pr-12">{citation}{t36}</div>;
    $[68] = citation;
    $[69] = t36;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t34 || $[72] !== t37) {
    t38 = <div className="bg-white border border-[#0F1C2E]/10 p-5 sm:p-6 md:p-8 mb-8 sm:mb-12">{t32}{t34}{t37}</div>;
    $[71] = t34;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t13 || $[75] !== t17 || $[76] !== t26 || $[77] !== t28 || $[78] !== t31 || $[79] !== t38) {
    t39 = <div className="flex-1">{t13}{t17}{t26}{t28}{t31}{t38}</div>;
    $[74] = t13;
    $[75] = t17;
    $[76] = t26;
    $[77] = t28;
    $[78] = t31;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== report.access) {
    t40 = <div className="bg-[#0F1C2E] p-6 sm:p-8 text-center">{report.access === "Open Access" ? <><FileText className="mx-auto mb-4 text-[#DF6D2A]" size={32} /><p className="font-display text-[20px] text-[#F4EFE6] mb-2">Full Report Available</p><p className="font-body text-[12px] text-white/50 mb-6">Open Access • PDF Format</p><button className="w-full font-body py-3.5 btn-primary text-[12px] tracking-[0.15em] uppercase hover:bg-white hover:text-[#0F1C2E] transition-colors flex items-center justify-center gap-2"><Download size={14} /> Download PDF</button></> : <><Lock className="mx-auto mb-4 text-red-400" size={32} /><p className="font-display text-[20px] text-[#F4EFE6] mb-2">Restricted Access</p><p className="font-body text-[12px] text-white/50 mb-6">Contact the publisher for access</p><Link href="/contact" className="block w-full font-body py-3.5 btn-primary text-[12px] tracking-[0.15em] uppercase hover:bg-white hover:text-[#0F1C2E] transition-colors text-center">Request Access</Link></>}</div>;
    $[81] = report.access;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] !== relatedReports) {
    t41 = relatedReports.length > 0 && <div><h3 className="font-body text-[10px] uppercase tracking-widest text-[#4C596B] mb-4 pb-2 border-b border-[#0F1C2E]/10">Related Reports</h3><div className="space-y-4">{relatedReports.map(_temp2)}</div></div>;
    $[83] = relatedReports;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] !== t40 || $[86] !== t41) {
    t42 = <div className="w-full lg:w-[300px] shrink-0"><div className="lg:sticky lg:top-32 space-y-6 sm:space-y-8">{t40}{t41}</div></div>;
    $[85] = t40;
    $[86] = t41;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== t39 || $[89] !== t42) {
    t43 = <section className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16 md:py-24"><div className="flex flex-col lg:flex-row gap-10 sm:gap-12 lg:gap-16">{t39}{t42}</div></section>;
    $[88] = t39;
    $[89] = t42;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] !== t43 || $[92] !== t8) {
    t44 = <main className="min-h-screen bg-[#F4EFE6]">{t8}{t43}</main>;
    $[91] = t43;
    $[92] = t8;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== t44 || $[95] !== t5) {
    t45 = <>{t5}{t44}</>;
    $[94] = t44;
    $[95] = t5;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  return t45;
}
function _temp2(rel) {
  return <Link key={rel.slug} href={`/reports/${rel.slug}`} className="block group"><p className="font-display text-[16px] sm:text-[18px] text-[#0F1C2E] group-hover:text-[#DF6D2A] transition-colors leading-tight mb-1">{rel.title}</p><p className="font-body text-[11px] text-[#4C596B]">{rel.year} • {rel.country}</p></Link>;
}
function _temp(tag) {
  return <span key={tag} className="font-body text-[11px] tracking-wide text-[#0F1C2E] border border-[#0F1C2E]/15 px-3 py-1.5 hover:bg-[#0F1C2E] hover:text-white transition-colors cursor-pointer">{tag}</span>;
}
export async function getStaticPaths() {
  const paths = REPORTS_DB.map(report => ({
    params: {
      slug: report.slug
    }
  }));
  return {
    paths,
    fallback: false
  };
}
export async function getStaticProps({
  params
}) {
  const report = getReportBySlug(params.slug);
  if (!report) return {
    notFound: true
  };
  const relatedReports = getRelatedReports(report.slug, report.country, report.type);
  return {
    props: {
      report,
      relatedReports
    }
  };
}